import React, { useEffect } from 'react';
import { FormProvider, FormState, useForm, UseFormReturn } from 'react-hook-form';
import { useHistory } from 'react-router';
import { login, registerPersonalDetails, registerRegisterFirm } from 'routes/routePaths';
import Navigation from '../Form/Navigation/Navigation';
import { validationSchema } from 'screens/BrokerPortal/screens/Registration/components/SearchFirmsStep/validationSchema';
import { FIELD, Form } from 'screens/BrokerPortal/screens/Registration/components/SearchFirmsStep/types';
import { State } from 'screens/BrokerPortal/screens/Registration/store/types';
import { FormInputWrapper, Select } from 'form/infrastructure/reactHookForm';
import { Option } from 'form/components/Select/Select';
import useYupValidationResolver from 'form/yup/reactHookFormResolver';
import { Firm } from 'screens/BrokerPortal/screens/Registration/api';
import { Nullable } from 'utils/types';
import { Step } from 'screens/BrokerPortal/screens/Registration/steps';
import { onlyDigits } from 'shared/regex/regex';
import BoxWrapper from '../Form/RegistrationFormTemplate/BoxWrapper/BoxWrapper';
import { Heading } from 'lmc-ui-library-rebrand';

interface Props {
  form: State['searchFirms']['form'];
  firms: State['searchFirms']['firms'];
  setSearchFirmsForm: (data: Form) => void;
  loadFirmsByFcaNumber: (fcaNumber: string) => any;
  setContextFirm: (firm: Nullable<Firm>) => void;
  setCurrentStep: (step: Step) => void;
}

export const ADD_NEW_FIRM_OPTION = 0;

const addNewFirmOption: Option = {
  selectedRender: (
    <span>
      ADD A NEW FIRM <span style={{ fontWeight: 400 }}>(continue to create your firm)</span>
    </span>
  ),
  label: 'ADD A NEW FIRM',
  render: <b style={{ fontWeight: 700 }}>ADD A NEW FIRM</b>,
  name: ADD_NEW_FIRM_OPTION.toString(),
  selected: false,
};

const SearchFirmsStep: React.FC<Props> = ({
  form,
  setSearchFirmsForm,
  loadFirmsByFcaNumber,
  firms,
  setContextFirm,
  setCurrentStep,
}) => {
  const history = useHistory();
  const methods: UseFormReturn<Form> & { formState: FormState<Form> & { errors: any } } = useForm<Form>({
    mode: 'onBlur',
    resolver: useYupValidationResolver(validationSchema()),
    defaultValues: form,
  });
  const {
    watch,
    handleSubmit,
    setValue,
    trigger,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    formState: { isValid },
  } = methods;
  const watchFcaId = watch(FIELD.FIRM_ID);

  useEffect(() => {
    setCurrentStep(Step.SEARCH_FIRMS);
  }, [setCurrentStep]);

  const onSubmit = handleSubmit((data) => setSearchFirmsForm(data));

  const handleBack = () => history.push(login);

  const handleContinue = async () => {
    if (!(await trigger())) {
      return;
    }
    await onSubmit();
    if (Number(watchFcaId) === ADD_NEW_FIRM_OPTION) {
      setContextFirm(null);
    } else {
      setContextFirm(firms.items.filter((firm) => firm.id === watchFcaId)[0]);
    }
    history.push(Number(watchFcaId) === ADD_NEW_FIRM_OPTION ? registerRegisterFirm : registerPersonalDetails);
  };

  const reloadSearchOptions = async (fcaNumber: string) => {
    if (!fcaNumber || fcaNumber.toString().length < 3) {
      return;
    }
    loadFirmsByFcaNumber(fcaNumber);
  };

  const firmsOptions = [
    ...firms.items.map((firm) => ({
      label: `${firm.fcaNumber} - ${firm.name}`,
      name: firm.id.toString(),
      selected: false,
    })),
    addNewFirmOption,
  ];

  return (
    <BoxWrapper>
      <FormProvider<Form> {...methods}>
        <Heading mode="title" weight="bold">
          Find your firm
        </Heading>
        <form onSubmit={onSubmit}>
          <FormInputWrapper name={FIELD.FIRM_ID} title="Search firm FCA number" newUi>
            <Select
              newUi
              options={firmsOptions}
              inputRegexp={onlyDigits}
              isLoading={firms.loading}
              minSearchLengthToShowDroplist={3}
              onSearchInputChange={reloadSearchOptions}
              onNewUiOptionClick={(option) => {
                setValue(FIELD.FIRM_ID, Number(option.name), { shouldDirty: true, shouldValidate: true });
              }}
            />
          </FormInputWrapper>
        </form>
        <Navigation onBack={handleBack} onContinue={handleContinue} continueDisabled={false} />
      </FormProvider>
    </BoxWrapper>
  );
};

export default SearchFirmsStep;
